// Where an image banner's picture goes (#607) and the CSS each fill compiles to. templateBuilder.js compiles
// templates with it and re-exports it; the widget imports this module alone (templateWithBannerFill), so it
// never pulls in templateBuilder.json or every layout's CSS. template_builder.go holds the same names and
// rules for the server side.

export const BANNER_IMAGE_VARIABLE = '--banner-image'
// Framing variables: the template sets the defaults, a pack may override them like the picture.
export const BANNER_POSITION_VARIABLE = '--banner-position'
export const BANNER_SIZE_VARIABLE = '--banner-size'
// A pack's own fill (issue 683): its schedule chose another fill than the template's. Stamped in template_vars
// as a `--` key so feed-api forwards it, but no template declares it, so it never reaches a stylesheet: the
// renderers read it and draw the template in that fill (templateWithBannerFill).
export const BANNER_FILL_VARIABLE = '--banner-fill'
// Where the picture goes: behind the header (the default, and every template saved before the
// choice existed) or behind the whole card under a scrim.
export const BANNER_FILL_HEADER = 'header'
export const BANNER_FILL_CARD = 'card'
export const BANNER_FILLS = [BANNER_FILL_HEADER, BANNER_FILL_CARD]
export const DEFAULT_BANNER_FILL = BANNER_FILL_HEADER
/** The fill a picture's settings ask for: the whole card, or the header for anything else. */
export function bannerFillOf(image) {
  return image?.fill === BANNER_FILL_CARD ? BANNER_FILL_CARD : BANNER_FILL_HEADER
}
// The native footer under a whole-card picture (#1038): tinted, the solid banner fade the card ends in (the default,
// and every template saved before the choice), or transparent, so the picture and its scrim run on under the odds
// button. A header picture has no footer choice: the card's own colors sit under its footer.
export const BANNER_FOOTER_TINTED = 'tinted'
export const BANNER_FOOTER_TRANSPARENT = 'transparent'
export const BANNER_FOOTERS = [BANNER_FOOTER_TINTED, BANNER_FOOTER_TRANSPARENT]
export const DEFAULT_BANNER_FOOTER = BANNER_FOOTER_TINTED
/** The footer a picture's settings ask for: transparent only under the whole card, tinted for anything else. */
export function bannerFooterOf(image) {
  return bannerFillOf(image) === BANNER_FILL_CARD && image?.footer === BANNER_FOOTER_TRANSPARENT ? BANNER_FOOTER_TRANSPARENT : BANNER_FOOTER_TINTED
}
// Framing: the point of the picture kept in view ("x% y%") and how much wider than the banner it is drawn.
export const DEFAULT_BANNER_POSITION = '50% 50%'
// The card fill starts flush right: the prompt puts the subject on the right, and a strip the
// picture leaves uncovered on a short, wide card falls under the solid left of the scrim.
const DEFAULT_CARD_POSITION = '100% 50%'
/** Where a picture starts for the fill, before an operator moves it. */
export function defaultBannerPosition(fill) {
  return fill === BANNER_FILL_CARD ? DEFAULT_CARD_POSITION : DEFAULT_BANNER_POSITION
}
export const MIN_BANNER_SCALE = 1
export const MAX_BANNER_SCALE = 3
export function bannerScaleOf(scale) {
  return typeof scale === 'number' && scale >= MIN_BANNER_SCALE && scale <= MAX_BANNER_SCALE ? Math.round(scale * 100) / 100 : MIN_BANNER_SCALE
}

// A banner replaces whatever header the layout draws. Its tag inverts the
// banner's own colors, and the split keeps the title inside the main-color zone.
export const BANNER_HEAD = '.head{padding:18px 16px;border:0;color:var(--header-text)}.tag{padding:3px 7px;border-color:transparent;background:var(--header-text);color:var(--header)}.subtitle{padding-top:10px}'
// The picture shows through on the right; the main color fades in from the
// left so the title stays readable, and takes over when there is no picture
// yet. The picture is drawn `scale` times the banner's width (a landscape
// picture at 1 already fills the band's height) with `position` kept in view;
// behind the whole card it is drawn `scale` times the card's height instead,
// since the card's height is what changes with its legs.
export function bannerImageSize(scale, fill = BANNER_FILL_HEADER) {
  const percent = `${Math.round(bannerScaleOf(scale) * 100)}%`
  return fill === BANNER_FILL_CARD ? `auto ${percent}` : `${percent} auto`
}
/** The color wash over a banner picture: solid `color` on the left, where the title sits, thinning to a tint on the right. The framing box draws the same wash. */
export function bannerOverlay(color) {
  return `linear-gradient(90deg,${color} 0 30%,color-mix(in srgb,${color} 55%,transparent) 62%,color-mix(in srgb,${color} 10%,transparent) 100%)`
}
export const IMAGE_BANNER_CSS = `${BANNER_HEAD}.head{min-height:76px;background:${bannerOverlay('var(--header)')},var(${BANNER_IMAGE_VARIABLE}) var(${BANNER_POSITION_VARIABLE})/var(${BANNER_SIZE_VARIABLE}) no-repeat,linear-gradient(135deg,var(--header),var(--header-end))}`
// The scrim never thins past this share of `color`: a title, summary or leg copy can run the full
// width of the card, so the photo may show through under text only this much. templateBuilder.test.js
// holds the header text and the secondary mix at 4.5:1 over the floor with a pure white or pure black
// photo behind it, for every stress palette.
export const CARD_SCRIM_FLOOR = 75
/** The scrim over a picture behind the whole card: solid `color` over the left, where most text sits, thinning to CARD_SCRIM_FLOOR towards the subject on the right. The framing box draws the same scrim. */
export function cardScrim(color) {
  return `linear-gradient(90deg,${color} 0 42%,color-mix(in srgb,${color} 85%,transparent) 64%,color-mix(in srgb,${color} ${CARD_SCRIM_FLOOR}%,transparent) 100%)`
}
// Secondary text over the card fill: the header text pulled this far (percent kept) towards the scrim
// color instead of an opacity, so its contrast on the scrim is a fixed, testable color.
export const CARD_SECONDARY_TEXT_SHARE = 92
const CARD_SECONDARY_TEXT = `color-mix(in srgb,var(--header-text) ${CARD_SECONDARY_TEXT_SHARE}%,var(--header-end))`
/** The whole-card picture under its scrim as one background value: the artwork's .card draws it, or the widget's card shell when the picture runs under the footer (#1038). */
export const CARD_PICTURE_BACKGROUND = `${cardScrim('var(--header-end)')},var(${BANNER_IMAGE_VARIABLE}) var(${BANNER_POSITION_VARIABLE})/var(${BANNER_SIZE_VARIABLE}) no-repeat,linear-gradient(135deg,var(--header),var(--header-end))`
// The text, divider and pill rules of every whole-card picture, whichever surface draws the picture.
const CARD_TEXT_CSS = '.head,.leg,div.placed{background:none}'
  + `.subtitle,.summary,.leg-copy small,.leg-event,.event,div.placed,.head .placed{color:${CARD_SECONDARY_TEXT};opacity:1}`
  + '.leg+.leg{border-top-color:color-mix(in srgb,var(--header-text) 22%,transparent)}'
  + '.leg-icon{background:color-mix(in srgb,var(--header-text) 14%,transparent)}'
  + '.leg-side,.head .placed{padding:2px 6px;border-radius:6px;background:var(--header-end)}'
// The picture behind the whole card (#607). The card carries the picture under a scrim of the banner
// fade (--header-end) and every surface the layout paints (the header band, soft-rows tiles, the accent
// border's counter band) goes, so the photo shows through the whole artwork. All text takes the header
// text color, whose contrast on the fade placePalette guarantees: secondary text is a mix of the two
// rather than an opacity, and the right-aligned odds and game, and a counter in the header, sit on an
// opaque pill of the fade where the scrim thins. No text is read against the photo itself.
export const IMAGE_CARD_CSS = `${BANNER_HEAD}.card{color:var(--header-text);background:${CARD_PICTURE_BACKGROUND}}${CARD_TEXT_CSS}`
// What tells the card shell to draw the picture under the footer (#1038): a declaration of its own, read with any
// spacing (hasFullCardPicture), so a hand edit of the other rules ("Edit the HTML and CSS by hand" starts from the compiled CSS)
// keeps the picture; deleting it hands the card back to the artwork alone.
export const FULL_CARD_PICTURE_MARKER = ':root{--banner-footer:transparent}'
const FULL_CARD_PICTURE = /--banner-footer\s*:\s*transparent\b/
const CSS_COMMENT = /\/\*[\s\S]*?(?:\*\/|$)/g
/** CSS without its comments: a declaration commented out by hand is not one the document has. */
export function withoutCssComments(css) {
  return typeof css === 'string' ? css.replace(CSS_COMMENT, '') : ''
}
/** Whether artwork CSS asks the card shell to draw its whole-card picture, under the footer too (#1038). */
export function hasFullCardPicture(cssCode) {
  return FULL_CARD_PICTURE.test(withoutCssComments(cssCode))
}
// The picture under the footer too (#1038, "Footer: Transparent"): the artwork paints no picture and no color at
// all, so the widget's card shell shows through it and draws CARD_PICTURE_BACKGROUND under the frame, the "See all"
// row and the native footer alike (PrepacksPresentation.tsx, data-full-picture). Every other card-fill rule is the
// same. A widget from before the choice draws this artwork over its plain surface, without the picture.
export const IMAGE_CARD_FULL_CSS = `${BANNER_HEAD}${FULL_CARD_PICTURE_MARKER}html,body{background:transparent}.card{color:var(--header-text);background:transparent}${CARD_TEXT_CSS}`
// Team marks (#572, templateBuilder.js legIconMarkup). Only templates with logos or jerseys get this CSS, so a template without
// either compiles to exactly what it did before. An empty token leaves
// src="", which hides that image; the first image with a source wins (logo,
// then jersey) and the glyph shows only when neither has one. Images are
// <img> tags, never CSS backgrounds: builder CSS carries no url().
// A logo that fails to load (404, or a host page CSP that blocks it) cannot
// fall back to the jersey: the frame runs no scripts. Chrome and Firefox draw
// ::after only on a broken image, so a cover in the cell's own color hides
// their broken-image icon and clip-path trims the 1px frame they draw around
// it: the cell reads as empty. A loaded image loses at most that 1px edge.
export const MARK_CSS = '.leg-icon>*{grid-area:1/1}.leg-logo,.leg-jersey{display:block;position:relative;width:24px;height:24px;object-fit:contain;clip-path:inset(1px)}.leg-jersey{width:28px;height:28px}.leg-logo::after,.leg-jersey::after{content:"";position:absolute;inset:0;background:var(--row)}.leg-icon img[src=""],.leg-icon:has(.leg-logo:not([src=""])) .leg-jersey,.leg-icon:has(img:not([src=""])) .leg-glyph{display:none}'
// Layouts that size or color the icon cell their own way.
export const LAYOUT_MARK_CSS = {
  'soft-rows': '.leg-logo::after,.leg-jersey::after{background:var(--bg)}',
  minimal: '.leg-logo,.leg-jersey{width:24px;height:24px}.leg-logo::after,.leg-jersey::after{background:var(--bg)}',
  compact: '.leg-logo,.leg-jersey{width:20px;height:20px}',
}

// With logos or jerseys on: the cover over a broken image takes the scrim color, not the card's.
export const IMAGE_CARD_MARK_CSS = '.card .leg-logo::after,.card .leg-jersey::after{background:var(--header-end)}'

// Where the pack title sits on an image banner and how it is aligned (#946). Over the picture, left-aligned, is
// what every template drew before the choice existed. template_builder.go holds the same names and rules.
export const TITLE_PLACEMENT_OVER = 'over'
export const TITLE_PLACEMENT_BELOW = 'below'
export const TITLE_PLACEMENT_NONE = 'none'
export const TITLE_PLACEMENTS = [TITLE_PLACEMENT_OVER, TITLE_PLACEMENT_BELOW, TITLE_PLACEMENT_NONE]
export const TITLE_ALIGNMENT_LEFT = 'left'
export const TITLE_ALIGNMENT_CENTER = 'center'
export const TITLE_ALIGNMENTS = [TITLE_ALIGNMENT_LEFT, TITLE_ALIGNMENT_CENTER]
export const DEFAULT_BANNER_TITLE = Object.freeze({ placement: TITLE_PLACEMENT_OVER, alignment: TITLE_ALIGNMENT_LEFT })

/** The title settings of a picture in `fill`: unknown values read as the defaults, and nothing sits below a picture behind the whole card. */
export function bannerTitleOf(title, fill) {
  let placement = TITLE_PLACEMENTS.includes(title?.placement) ? title.placement : TITLE_PLACEMENT_OVER
  if (placement === TITLE_PLACEMENT_BELOW && fill === BANNER_FILL_CARD) placement = TITLE_PLACEMENT_OVER
  return { placement, alignment: TITLE_ALIGNMENTS.includes(title?.alignment) ? title.alignment : TITLE_ALIGNMENT_LEFT }
}

// The picture as a band of its own above the header (#946: "Below image" and "No title"). .card::before draws it
// as the card's first flex item, so every placement shares one markup and a pack's picture, focus and zoom frame it
// as they framed the header (same height as the header fill's minimum, same framing variables). The header then
// sits on the card background in the card's own colors with the accent tag, whatever the layout (the banner no
// longer replaces it, it sits above it); a hero header's counter and a badge move with the band. A pack whose
// picture is removed shows the template's gradient in the band, as a header picture shows the card's colors (#606).
const BAND_HEIGHT = 76
export const IMAGE_BAND_CSS = `.card::before{content:"";display:block;flex-shrink:0;height:${BAND_HEIGHT}px;background:var(${BANNER_IMAGE_VARIABLE}) var(${BANNER_POSITION_VARIABLE})/var(${BANNER_SIZE_VARIABLE}) no-repeat,linear-gradient(135deg,var(--header),var(--header-end))}`
  + '.head,.card .head{background:none;color:inherit}.tag{padding:3px 7px;border-color:transparent;background:var(--accent);color:var(--accent-text)}'
  + `.card .head .placed{top:${BAND_HEIGHT + 22}px}.card:has(>.placed-badge) .head{padding-right:16px}.card:has(>.placed-badge) .tag{max-width:60%}`

/** The wash under a centered title over the header picture: `color` solid across the middle, never thinner than CARD_SCRIM_FLOOR at either edge. The framing box draws the same wash. */
export function centerWash(color) {
  const edge = `color-mix(in srgb,${color} ${CARD_SCRIM_FLOOR}%,transparent)`
  return `linear-gradient(90deg,${edge} 0,${color} 30% 70%,${edge} 100%)`
}
// Over the header picture a centered title sits where the left-to-right wash has thinned, so the wash turns even,
// in the banner fade color at the floor templateBuilder.test.js proves readable over any photo.
export const IMAGE_BANNER_CENTER_CSS = `.head{background:${centerWash('var(--header-end)')},var(${BANNER_IMAGE_VARIABLE}) var(${BANNER_POSITION_VARIABLE})/var(${BANNER_SIZE_VARIABLE}) no-repeat,linear-gradient(135deg,var(--header),var(--header-end))}`
// Centered: the title takes a line of its own (a row header wraps; a hero header's title already has one and
// stretches to the column), the tag, counter and game keep their places. The title is ordered after a header
// counter, which comes after it in the markup, so the counter stays on the tag's row instead of wrapping below the
// title; the game follows the title as before (only hero headers show it, and their counter is out of the flow).
export const TITLE_CENTER_CSS = '.head{flex-wrap:wrap}.title{flex-basis:100%;order:1;text-align:center}.event{order:2}.card .title{align-self:stretch}'
// A centered title over the picture beside the bets-placed badge (header or whole-card fill; a band carries the
// badge itself). BASE_CSS keeps the whole header 184px clear of the badge, which would center the title in what is
// left beside it. On a line of its own the title sits below the tag's row, the only row the badge overlaps, so the
// header gives that room back and only the tag stays clear of the badge (at most 46% wide, 12px from the edge). A
// header without a tag starts its title under the badge instead (COUNTER_TITLE_OFFSET, measured for row and hero
// heads alike). Without a badge it matches nothing.
const COUNTER_TITLE_OFFSET = 24
export const TITLE_CENTER_BADGE_CSS = '.card:has(>.placed-badge) .head{padding-right:16px}.card:has(>.placed-badge) .tag{max-width:calc(100% - 184px)}'
  + `.card:has(>.placed-badge) .head:not(:has(.tag))>.title{margin-top:${COUNTER_TITLE_OFFSET}px}`
// A title in a hero header with the counter in it, centered (#946) or left-aligned (#950): that counter is out of the
// flow at the header's top right, and the title starts at the same top when no tag sits above it, so a long one ran
// under the counter; it starts under it instead (the same offset, measured for every fill and the band). With a tag
// the title is on the row below; a pack nobody has copied has no counter, and the title stays where it is. Written
// only for those layouts with the counter in the header (titleUnderCounter in templateBuilder.js), after the title's
// own rules, since it is the same in either fill.
export const TITLE_COUNTER_CSS = `.card:has(.head .placed) .head:not(:has(.tag))>.title{margin-top:${COUNTER_TITLE_OFFSET}px}`
// No title (#946): templateBuilder.js writes the header only while something may show in it (the tag, a header
// counter, a hero header's game), but a pack can still leave it empty: a pack nobody has copied drops the counter
// and a blank game hides. Such a header hides too, so no padded empty box sits under the band or over the card
// picture, and the tagline after it keeps the room it would have had from the band. `showsEvent` is whether the
// layout shows the game in the header (only the hero layouts; elsewhere the game is always hidden, so it keeps
// nothing). The same in either fill, so templateWithBannerFill leaves it alone.
export function titleNoneCss(showsEvent) {
  const empty = `.head:not(:has(.tag,.placed${showsEvent ? ',.event:not(:empty)' : ''}))`
  return `${empty}{display:none}${empty}+.subtitle{padding-top:10px}`
}
// No title under a whole-card picture: the badge has no band to sit on, so the header kept for it (templateBuilder.js
// writes one whenever the counter is a badge, even with nothing else in it) stays at least the badge's height and the
// content starts under it. It ties with the hero hide rule in titleNoneCss(true) (0,3,0), so it comes right after
// that block; templateWithBannerFill adds it for the card fill and strips it for the header, where the band holds
// the badge.
export const TITLE_NONE_CARD_BADGE_CSS = '.card:has(>.placed-badge) .head{display:flex;min-height:44px}'
// No title and no header in the markup at all: the tagline comes first under the band, which is a pseudo-element no
// sibling rule can see, so the room is written outright.
export const TITLE_NONE_HEADLESS_CSS = '.subtitle{padding-top:10px}'

// The native footer under the artwork (branding components.prepackActions.background) by fill, as templateBuilder.js
// nativeBranding writes it: the scrim's color under a card picture, else the row tint with an accent border (the
// accent-border layout or the card border feature) and the card's color without one. An operator's footer (the
// shell setting) stays whatever the fill.
/**
 * The footer under a tinted whole-card picture: the scrim's end color. The widget's card shell reads it too, to tell
 * the rows it draws above the footer ("See all N bets", the tag row) that they sit on the scrim (issue 1092).
 */
export const CARD_PICTURE_FOOTER = 'var(--header-end)'
// Under a picture that runs on under the footer (#1038) the footer paints nothing of its own.
const FULL_CARD_FOOTER = 'transparent'
const ROW_FOOTER = 'var(--row)'
const PLAIN_FOOTER = 'var(--bg)'
const FOOTER_SHELL_PATH = 'components.prepackActions.background'
// Team marks are drawn in an icon cell carrying data-mark (templateBuilder.js legIconMarkup).
const TEAM_MARKS = /\bdata-mark="/
// The pack title (templateBuilder.js builderHtml); a template whose title placement is "none" has none (#946).
const TITLE_MARKUP = /\bclass="title"/

function framedVariables(variables, fill) {
  if (!Array.isArray(variables)) return variables
  return variables.map((variable) => {
    if (variable?.key === BANNER_POSITION_VARIABLE) return { ...variable, value: defaultBannerPosition(fill) }
    if (variable?.key === BANNER_SIZE_VARIABLE) return { ...variable, value: bannerImageSize(1, fill) }
    return variable
  })
}

/**
 * Whether the layout draws an accent border and whether the operator set the footer, for one mode: exactly from the
 * builder choices when the template carries them (Studio), else read back from the compiled branding (the widget:
 * feed-api strips the builder). There, a footer that is one of the layout's own values is taken as the layout's,
 * and an accent border shows as the accent border color or a border wider than the plain card's 1px; an operator's
 * footer typed as one of those values, or a thin card border recolored by the operator, reads wrong there.
 */
function footerRule(builder, mode, branding, from) {
  if (builder && typeof builder === 'object' && typeof builder.layout === 'string') {
    return {
      accentBorder: builder.layout === 'accent-border' || builder.features?.cardBorder === true,
      overridden: Boolean(builder.shell?.[mode]?.[FOOTER_SHELL_PATH]),
    }
  }
  const background = branding?.components?.prepackActions?.background
  const width = branding?.layout?.card?.borderWidth
  return {
    accentBorder: branding?.colors?.borderColor === 'var(--accent)' || (typeof width === 'string' && width !== '1px'),
    overridden: from === BANNER_FILL_CARD ? background !== CARD_PICTURE_FOOTER && background !== FULL_CARD_FOOTER : background !== ROW_FOOTER && background !== PLAIN_FOOTER,
  }
}

function footerFor(builder, mode, branding, from, fill) {
  if (!branding?.components?.prepackActions?.background) return branding
  const { accentBorder, overridden } = footerRule(builder, mode, branding, from)
  if (overridden) return branding
  const next = fill === BANNER_FILL_CARD ? CARD_PICTURE_FOOTER : accentBorder ? ROW_FOOTER : PLAIN_FOOTER
  return { ...branding, components: { ...branding.components, prepackActions: { ...branding.components.prepackActions, background: next } } }
}

const filledTemplates = new WeakMap()

/**
 * The card fill's mark CSS put where compileBuilderTemplate puts it: right after the team mark block (MARK_CSS and
 * the layout's own LAYOUT_MARK_CSS), so CSS added by hand after the generated CSS (#998) stays last. CSS without the
 * mark block gets it at the end.
 */
function withCardMarkCss(css) {
  const at = css.indexOf(MARK_CSS)
  if (at < 0) return css + IMAGE_CARD_MARK_CSS
  let end = at + MARK_CSS.length
  const layoutMarks = Object.values(LAYOUT_MARK_CSS).find((block) => css.startsWith(block, end))
  if (layoutMarks) end += layoutMarks.length
  return css.slice(0, end) + IMAGE_CARD_MARK_CSS + css.slice(end)
}

/**
 * A compiled template drawn with its picture in `fill` rather than the fill it was compiled for (issue 683: a
 * schedule can override its template's fill, and stamps it on each pack as BANNER_FILL_VARIABLE). It works on
 * the compiled template alone, since the widget never sees `builder`: the fill is found by the exact CSS it
 * compiled to, that CSS is swapped for the other fill's, the framing variables go back to that fill's defaults
 * (as the template builder does when the fill is switched) and the native footer follows the fill unless an
 * operator styled it (footerRule). The result is what compileBuilderTemplate gives for the same choices in the other fill.
 * CSS added by hand after the generated CSS (#998, builder.css) is kept as it is, last: only the generated part is
 * swapped. A template without an image banner, with hand-edited code, already in `fill`, or an unknown `fill` comes
 * back as it is. The widget never sees `builder`, so it cannot tell hand-edited code seeded from the builder's
 * CSS: feed-api sends a pack's fill only while its template honours it (keepHonouredBannerFill). Results are
 * cached per template object (templates are replaced, never edited in place).
 * @template T
 * @param {T} template
 * @param {unknown} fill
 * @returns {T}
 */
export function templateWithBannerFill(template, fill) {
  if (!template || typeof template !== 'object' || (fill !== BANNER_FILL_HEADER && fill !== BANNER_FILL_CARD)) return template
  const cssCode = typeof template.cssCode === 'string' ? template.cssCode : ''
  // A card picture is drawn by the artwork (IMAGE_CARD_CSS) or, under a transparent footer, by the card shell (#1038).
  const cardCss = cssCode.includes(IMAGE_CARD_FULL_CSS) ? IMAGE_CARD_FULL_CSS : cssCode.includes(IMAGE_CARD_CSS) ? IMAGE_CARD_CSS : ''
  // A header picture is drawn behind the header, or as a band above it when the title sits below it or is gone (#946).
  const from = cardCss ? BANNER_FILL_CARD
    : cssCode.includes(IMAGE_BANNER_CSS) || cssCode.includes(IMAGE_BAND_CSS) ? BANNER_FILL_HEADER : ''
  if (!from || from === fill || template.builder?.code) return template
  let cached = filledTemplates.get(template)
  if (!cached) filledTemplates.set(template, cached = {})
  if (cached[fill]) return cached[fill]
  let nextCss
  if (fill === BANNER_FILL_CARD) {
    // Nothing sits below a picture behind the whole card, and its scrim stands under a centered title as it is. A
    // centered title below a band comes over the picture, so it takes the badge rules a centered title over it has.
    nextCss = cssCode.replace(cssCode.includes(IMAGE_BAND_CSS) ? IMAGE_BAND_CSS : IMAGE_BANNER_CSS, () => IMAGE_CARD_CSS).replace(IMAGE_BANNER_CENTER_CSS, () => '')
    if (nextCss.includes(TITLE_CENTER_CSS) && !nextCss.includes(TITLE_CENTER_BADGE_CSS)) nextCss = nextCss.replace(TITLE_CENTER_CSS, () => TITLE_CENTER_CSS + TITLE_CENTER_BADGE_CSS)
    // Without a title the header kept for a badge holds it off the content, as the card compiles it.
    const none = TITLE_MARKUP.test(template.htmlCode || '') ? '' : [titleNoneCss(true), titleNoneCss(false)].find((block) => nextCss.includes(block))
    if (none && !nextCss.includes(TITLE_NONE_CARD_BADGE_CSS)) nextCss = nextCss.replace(none, () => none + TITLE_NONE_CARD_BADGE_CSS)
    if (TEAM_MARKS.test(template.htmlCode || '')) nextCss = withCardMarkCss(nextCss)
  } else {
    // Back behind the header the title goes over the picture (a card template never had it below), with the even
    // wash under a centered one (a card's centered title always carries the badge rules, and the wash follows them);
    // a card without a title takes the band, as "No title" compiles for the header.
    nextCss = cssCode.replace(cardCss, () => (TITLE_MARKUP.test(template.htmlCode || '') ? IMAGE_BANNER_CSS : IMAGE_BAND_CSS))
    const centered = TITLE_CENTER_CSS + TITLE_CENTER_BADGE_CSS
    if (nextCss.includes(centered)) nextCss = nextCss.replace(centered, () => centered + IMAGE_BANNER_CENTER_CSS)
    nextCss = nextCss.replace(TITLE_NONE_CARD_BADGE_CSS, () => '')
    // Only a template with team marks compiled the card mark CSS; a markless one keeps that rule if it was added by hand.
    if (TEAM_MARKS.test(template.htmlCode || '')) nextCss = nextCss.replace(IMAGE_CARD_MARK_CSS, () => '')
  }
  const variants = template.variants && typeof template.variants === 'object'
    ? Object.fromEntries(Object.entries(template.variants).map(([mode, variant]) => [mode, variant && typeof variant === 'object'
      ? { ...variant, cssVars: framedVariables(variant.cssVars, fill), branding: footerFor(template.builder, mode, variant.branding, from, fill) }
      : variant]))
    : template.variants
  const result = { ...template, cssCode: nextCss, variants }
  if ('cssVars' in template) {
    // A compiled template's own variables are its dark variant's.
    result.cssVars = template.cssVars === template.variants?.dark?.cssVars ? variants.dark.cssVars : framedVariables(template.cssVars, fill)
  }
  const image = template.builder?.image
  if (image && typeof image === 'object') {
    // The footer choice belongs to the whole card, and a picture put there by its schedule is tinted (#1038).
    const { footer: _footer, ...rest } = image
    result.builder = { ...template.builder, image: { ...rest, fill, ...(fill === BANNER_FILL_CARD ? { footer: DEFAULT_BANNER_FOOTER } : {}), position: defaultBannerPosition(fill), scale: MIN_BANNER_SCALE }, title: bannerTitleOf(template.builder.title, fill) }
  }
  cached[fill] = result
  return result
}
